/* eslint-disable import/no-cycle */
import {action, decorate, observable, runInAction} from 'mobx';
import {compact} from 'lodash';
import {Api} from '../middleware/api';
import {EntityStore} from './entityStore';
import {Transition} from '../models/transition';

export const TransitionStoreActions = {
  performTransition: 'performTransition',
};

export class TransitionStore extends EntityStore {
  transitions = [];
  performedTransition = 0;

  constructor(rootStore) {
    super(rootStore, 'transitions', Api.transitions, Transition);
  }

  performTransition({workplaceId, body}, paramOptions = {}) {
    const options = {
      action: TransitionStoreActions.performTransition,
      ...paramOptions,
    };

    this.addPendingAction(options.action);

    return this.api
      .performTransition({workplaceId, ...body})
      .then(({data}) => {
        runInAction(() => this.removePendingAction(options.action));

        this.performedTransition += 1;
        return data;
      })
      .catch((e) => this.handleApiError(e, options.action));
  }

  getValidTransitionsFromCurrentStates(operationStateId, workplaceStateId) {
    return this.transitions.filter((transition) => this
      .transitionValid(transition, [operationStateId], [workplaceStateId]));
  }

  // eslint-disable-next-line class-methods-use-this
  transitionValid(
    transition,
    operationStateIds,
    workplaceStateIds
  ) {
    const everyAtLeastOne = (arr, f) => arr.length > 0 && arr.every(f);
    const sameId = (expected) => (obj) => obj.id === expected;
    const operationStatePresent = compact(operationStateIds).length > 0;

    const workplacesValid = transition.workplaceToState === null || everyAtLeastOne(workplaceStateIds,
      (prevStateId) => transition.workplaceFromStates.some(sameId(prevStateId)));

    const workplaceExclusivityValid = !transition.isWorkplaceExclusive
      || (transition.isWorkplaceExclusive && !operationStatePresent);

    const startingOperationRequired = transition.startingOperationToState !== null;
    const activeOperationRequired = transition.activeOperationToState !== null
      && !(everyAtLeastOne(operationStateIds, (prevStateId) => prevStateId === null));
    const noOperationTransition = (!transition.startingOperationToState && !transition.activeOperationToState);

    let operationsValid;
    if ((!startingOperationRequired && !activeOperationRequired) || noOperationTransition) {
      operationsValid = true;
    } else {
      const startingOperationsValid = !startingOperationRequired || everyAtLeastOne(operationStateIds,
        (prevStateId) => ((prevStateId === null)
          ? transition.startingOperationFromStates.some((opState) => opState.isInitial)
          : transition.startingOperationFromStates.some(sameId(prevStateId))));

      const activeOperationsValid = !activeOperationRequired || everyAtLeastOne(operationStateIds,
        (prevStateId) => transition.activeOperationFromStates.some(sameId(prevStateId)));

      operationsValid = startingOperationsValid && activeOperationsValid;
    }
    return workplacesValid && operationsValid && workplaceExclusivityValid;
  }
}

decorate(TransitionStore, {
  transitions: observable,
  performTransition: action,
  performedTransition: observable,
});

/**
 * @typedef {Object} CustomActionExecutorResponse
 * @property {bool} success
 * @property {bool} isTransitionAllowed
 * @property {string} message
 */

/**
 * custom action translations:
 * t('transition.customActionError.info')
 * t('transition.customActionError.warning')
 * t('transition.customActionError.error')
 *
 * @typedef {"info" | "warning" | "error"} CustomActionExecutionResponseType
 */

/**
 * @typedef {Object} CustomActionExecutorResponseAdditional
 * @property {number} customActionId
 * @property {bool} additionalPayload
 * @property {CustomActionExecutionResponseType} type
 */

/**
 * @typedef {CustomActionExecutorResponse & CustomActionExecutorResponseAdditional} CustomActionExecutorResponseExtended
 */

/**
 * @typedef {"PRE" | "POST"} CustomActionType
 */

/**
 * @typedef {Object} PerformTransitionResponse
 * @property {CustomActionType} customActionType
 * @property {Array<CustomActionExecutorResponseExtended>} errors
 */
